"use client";

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const products = [
  {
    name: '彩超',
    nameEn: 'Colour Ultrasound',
    desc: '高解析度彩色多普勒超音波系統，提供精確的血流動力學評估及組織結構影像，廣泛應用於心臟科、婦產科及腹部檢查。',
    features: ['彩色多普勒血流成像', '高清晰度顯示', '多探頭支援', '便攜式設計'],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 10l4.553-2.069A1 1 0 0121 8.867V15.133a1 1 0 01-1.447.902L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    name: '數位超音波',
    nameEn: 'Digital Ultrasound',
    desc: '先進數位超音波成像系統，配備智能化操作介面，提供高品質的二維及三維影像，適用於多種臨床診斷場景。',
    features: ['數位化全程記錄', '3D/4D 成像', '智能測量工具', '網絡連接功能'],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
      </svg>
    ),
  },
];

export default function ImagingSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="imaging" ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-8xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
          <div className="reveal-on-scroll flex justify-center">
            <span className="section-label">醫學影像 · Medical Imaging</span>
          </div>
          <div className="reveal-on-scroll reveal-delay-1">
            <div className="w-12 h-1 rounded mx-auto mb-5" style={{ background: 'linear-gradient(90deg, #2472A4, #1E5F8E)' }} />
            <h2 className="font-display font-800 text-foreground" style={{ fontWeight: 800, fontSize: 'clamp(28px, 3.5vw, 44px)' }}>
              醫學影像設備
              <br />
              <span style={{ color: 'var(--color-secondary)' }}>Medical Imaging</span>
            </h2>
          </div>
          <p className="reveal-on-scroll reveal-delay-2 text-muted leading-relaxed" style={{ fontSize: '16px' }}>
            提供先進的超音波影像設備，結合高解析度成像技術與智能化操作系統，為臨床診斷提供精確、可靠的影像支援。
          </p>
        </div>

        {/* Two product cards — asymmetric */}
        <div className="grid lg:grid-cols-2 gap-8">
          {products?.map((p, idx) => (
            <div key={p?.name} className={`reveal-on-scroll reveal-delay-${idx + 1} rounded-3xl overflow-hidden border border-border bg-white card-hover`}>
              {/* Image */}
              <div className="relative" style={{ height: '260px' }}>
                <AppImage
                  src={
                    idx === 0
                      ? 'https://images.pexels.com/photos/7088530/pexels-photo-7088530.jpeg?auto=compress&cs=tinysrgb&w=800'
                      : 'https://images.pexels.com/photos/4225880/pexels-photo-4225880.jpeg?auto=compress&cs=tinysrgb&w=800'
                  }
                  alt={idx === 0 ? 'Colour ultrasound equipment displaying cardiac imaging' : 'Digital ultrasound machine in a clinical examination room'}
                  fill
                  className="object-cover"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(36,114,164,0.7) 0%, transparent 60%)' }} />
                <div className="absolute bottom-4 left-5">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-700 text-white" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)', fontWeight: 700 }}>
                    {p?.nameEn}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-7 space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#EBF3FA', color: '#2472A4' }}>
                    {p?.icon}
                  </div>
                  <div>
                    <h3 className="font-display font-800 text-foreground" style={{ fontWeight: 800, fontSize: '20px' }}>{p?.name}</h3>
                    <p className="text-muted text-sm">{p?.nameEn}</p>
                  </div>
                </div>

                <p className="text-muted leading-relaxed" style={{ fontSize: '14px' }}>{p?.desc}</p>

                {/* Features */}
                <div className="grid grid-cols-2 gap-2">
                  {p?.features?.map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm text-foreground">
                      <svg className="w-4 h-4 flex-shrink-0 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span style={{ fontWeight: 500, fontSize: '13px' }}>{f}</span>
                    </div>
                  ))}
                </div>

                {/* Application tags */}
                <div className="pt-4 border-t border-border">
                  <p className="text-xs text-muted mb-2 font-600" style={{ fontWeight: 600 }}>應用科室</p>
                  <div className="flex flex-wrap gap-2">
                    {(idx === 0 ? ['心臟科', '婦產科', '腹部超音波', '血管科'] : ['放射科', '急診超音波', '骨科', '泌尿科'])?.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 rounded-lg text-xs font-600 text-secondary" style={{ background: '#EBF3FA', fontWeight: 600 }}>{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}